import { useEffect, useState } from "react";
import ModalV2 from "../../ModalV2";
import useModal from "../../../hooks/useModal";
import useSourceCode from "../../../hooks/useSourceCode";
import useNetworks from "../../../hooks/useNetworks";
import toast from "react-hot-toast";
import { Button } from "@oc-labs/wt-storybook";
import { X, Plus, Trash2, Rocket, Copy, CheckCircle2 } from "lucide-react";
import PropTypes from "prop-types";

const ARG_TYPES = ["string", "number", "address", "bool"];

const coerceArg = ({ type, value }) => {
  if (type === "number") {
    if (value === "" || value == null) return 0;
    const n = Number(value);
    if (Number.isNaN(n)) throw new Error(`"${value}" is not a valid number`);
    return n;
  }
  if (type === "bool") return value === "true" || value === true;
  return String(value ?? "");
};

const newArg = (overrides = {}) => ({
  id: crypto.randomUUID(),
  type: "string",
  value: "",
  name: "",
  solidityType: "",
  ...overrides,
});

const stripSolidityComments = (src) =>
  src.replace(/\/\/.*$/gm, "").replace(/\/\*[\s\S]*?\*\//g, "");

const solidityTypeToArgType = (solType) => {
  if (!solType) return "string";
  const t = solType.replace(/\s+payable$/, "").trim();
  if (t === "address") return "address";
  if (t === "bool") return "bool";
  if (/^(u?int)(\d*)$/.test(t)) return "number";
  return "string";
};

const parseConstructorParams = (paramsStr) => {
  const raw = paramsStr.trim();
  if (!raw) return [];
  // Split on commas that aren't inside parens/brackets (structs/arrays rarely show up here but be safe).
  const parts = [];
  let depth = 0;
  let buf = "";
  for (const ch of raw) {
    if (ch === "(" || ch === "[") depth++;
    else if (ch === ")" || ch === "]") depth--;
    if (ch === "," && depth === 0) {
      parts.push(buf.trim());
      buf = "";
    } else {
      buf += ch;
    }
  }
  if (buf.trim()) parts.push(buf.trim());

  return parts.map((p) => {
    // Drop data location keywords so we're left with "<type> <name>".
    const cleaned = p.replace(/\b(memory|calldata|storage)\b/g, " ").replace(/\s+/g, " ").trim();
    const tokens = cleaned.split(" ");
    let name = "";
    let solType = cleaned;
    if (tokens.length >= 2) {
      name = tokens[tokens.length - 1];
      solType = tokens.slice(0, -1).join(" ");
    }
    return {
      name,
      solidityType: solType,
      type: solidityTypeToArgType(solType),
    };
  });
};

const extractConstructorArgs = (source, fileName) => {
  if (!source) return null;
  const clean = stripSolidityComments(source);
  const contractName = (fileName || "").replace(/\.sol$/i, "");

  // Collect every constructor and tag it with its enclosing contract name (nearest preceding `contract X`).
  const ctorRegex = /constructor\s*\(/g;
  const candidates = [];
  let m;
  while ((m = ctorRegex.exec(clean)) !== null) {
    // Balanced-paren scan for the argument list.
    let i = m.index + m[0].length;
    let depth = 1;
    while (i < clean.length && depth > 0) {
      const ch = clean[i];
      if (ch === "(") depth++;
      else if (ch === ")") {
        depth--;
        if (depth === 0) break;
      }
      i++;
    }
    if (depth !== 0) continue;
    const paramsStr = clean.slice(m.index + m[0].length, i);

    // Nearest preceding `contract X` declaration.
    const before = clean.slice(0, m.index);
    const contractMatches = [...before.matchAll(/\bcontract\s+(\w+)/g)];
    const enclosing = contractMatches.length ? contractMatches[contractMatches.length - 1][1] : "";

    candidates.push({ paramsStr, enclosing });
  }

  if (!candidates.length) return [];

  const preferred =
    (contractName && candidates.find((c) => c.enclosing === contractName)) ||
    candidates[candidates.length - 1];

  return parseConstructorParams(preferred.paramsStr);
};

export default function DeploySourceModal({ onDeployed }) {
  const { close, data, isOpen } = useModal("deploy-source-modal");
  const { adminDeploySource, adminSourceCodeByName } = useSourceCode();
  const { adminNetworks } = useNetworks();
  const [networks, setNetworks] = useState([]);
  const [chainId, setChainId] = useState("");
  const [args, setArgs] = useState([]);
  const [deploying, setDeploying] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const [detected, setDetected] = useState(null);

  const sourceName = data?.name;

  useEffect(() => {
    if (!isOpen) return;
    setChainId("");
    setArgs([]);
    setResult(null);
    setDeploying(false);
    setDetected(null);
    adminNetworks()
      .then((res) => setNetworks(Array.isArray(res) ? res : []))
      .catch(() => setNetworks([]));

    if (!sourceName) return;
    setDetecting(true);
    const cached = data?.content;
    const loader = cached
      ? Promise.resolve(cached)
      : adminSourceCodeByName(sourceName).then((res) => res?.content ?? res?.body ?? "");
    loader
      .then((content) => {
        const parsed = extractConstructorArgs(content, sourceName);
        if (!parsed) {
          setDetected({ ok: false, reason: "empty" });
          return;
        }
        if (parsed.length === 0) {
          setDetected({ ok: true, count: 0 });
          return;
        }
        setArgs(parsed.map((p) => newArg({
          type: p.type,
          name: p.name,
          solidityType: p.solidityType,
        })));
        setDetected({ ok: true, count: parsed.length });
      })
      .catch(() => setDetected({ ok: false, reason: "error" }))
      .finally(() => setDetecting(false));
  }, [isOpen, sourceName]);

  const handleDeploy = async () => {
    if (!sourceName) return;
    if (!chainId) {
      toast.error("Pick a network");
      return;
    }
    let coerced;
    try {
      coerced = args.map(coerceArg);
    } catch (err) {
      toast.error(err.message);
      return;
    }
    setDeploying(true);
    try {
      const res = await adminDeploySource(sourceName, {
        chainId: Number(chainId),
        constructorArguments: coerced,
      });
      toast.success("Contract deployed");
      setResult(res);
      onDeployed?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Deploy failed");
    } finally {
      setDeploying(false);
    }
  };

  const copy = (key, value) => {
    if (value == null) return;
    navigator.clipboard.writeText(String(value));
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <ModalV2 name="deploy-source-modal" className="w-[min(720px,95vw)] max-h-[90vh]">
      <div className="flex flex-col max-h-[85vh] w-full">
        <div className="shrink-0 p-6 border-b border-gray-300 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl">
          <div className="flex justify-between items-start">
            <div className="flex flex-col gap-1">
              <h1 className="text-white text-2xl font-black flex items-center gap-2">
                <Rocket size={22} /> Deploy Contract
              </h1>
              <p className="text-blue-100 text-sm">
                Deploy <span className="font-mono">{sourceName}</span> on-chain.
              </p>
            </div>
            <button className="text-gray-200 hover:text-white" onClick={() => close()}>
              <X />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-5">
          {result ? (
            <ResultPanel result={result} copy={copy} copied={copied} />
          ) : (
            <>
              <label className="flex flex-col gap-2">
                <p className="text-gray-900 text-sm font-medium">Network</p>
                <select
                  className="w-full rounded-lg text-gray-900 focus:outline-0 focus:ring-2 focus:ring-primary border border-gray-300 bg-white h-12 px-4 text-base"
                  value={chainId}
                  onChange={(e) => setChainId(e.target.value)}
                >
                  <option value="">Select network…</option>
                  {networks.map((n) => (
                    <option key={n.id} value={n.chainId}>
                      {n.name} {n.chainId ? `(chainId ${n.chainId})` : ""}
                    </option>
                  ))}
                </select>
                {networks.length === 0 && (
                  <p className="text-xs text-gray-500">No networks configured.</p>
                )}
              </label>

              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <p className="text-gray-900 text-sm font-medium">Constructor Arguments</p>
                  <button
                    type="button"
                    onClick={() => setArgs([...args, newArg()])}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                  >
                    <Plus size={14} /> Add argument
                  </button>
                </div>

                {detecting && (
                  <p className="text-xs text-gray-500 italic">Detecting constructor from source…</p>
                )}
                {!detecting && detected?.ok && detected.count > 0 && (
                  <p className="text-xs text-blue-700">
                    Auto-filled {detected.count} argument{detected.count === 1 ? "" : "s"} from the
                    contract's constructor. Review values before deploying.
                  </p>
                )}
                {!detecting && detected?.ok && detected.count === 0 && (
                  <p className="text-xs text-gray-500 italic">
                    Constructor takes no parameters (or none detected). Add any manually if needed.
                  </p>
                )}
                {!detecting && detected && !detected.ok && (
                  <p className="text-xs text-amber-700">
                    Couldn't auto-detect constructor from source. Add arguments manually.
                  </p>
                )}

                {!detecting && args.length === 0 && !detected && (
                  <p className="text-xs text-gray-500 italic">
                    No arguments. Click "Add argument" if your constructor takes parameters.
                  </p>
                )}

                <div className="flex flex-col gap-2">
                  {args.map((arg, idx) => (
                    <div key={arg.id} className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 w-6 text-right">{idx}</span>
                      <select
                        className="rounded-lg border border-gray-300 bg-white h-10 px-3 text-sm"
                        value={arg.type}
                        onChange={(e) =>
                          setArgs(args.map((a) => (a.id === arg.id ? { ...a, type: e.target.value, value: "" } : a)))
                        }
                      >
                        {ARG_TYPES.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                      {arg.type === "bool" ? (
                        <select
                          className="flex-1 rounded-lg border border-gray-300 bg-white h-10 px-3 text-sm"
                          value={String(arg.value)}
                          onChange={(e) =>
                            setArgs(args.map((a) => (a.id === arg.id ? { ...a, value: e.target.value } : a)))
                          }
                        >
                          <option value="">— select —</option>
                          <option value="true">true</option>
                          <option value="false">false</option>
                        </select>
                      ) : (
                        <input
                          className="flex-1 rounded-lg border border-gray-300 bg-white h-10 px-3 text-sm font-mono"
                          type={arg.type === "number" ? "number" : "text"}
                          placeholder={
                            arg.name
                              ? `${arg.solidityType || arg.type} ${arg.name}`
                              : arg.type === "address"
                              ? "0x..."
                              : arg.type === "number"
                              ? "18"
                              : "value"
                          }
                          value={arg.value}
                          onChange={(e) =>
                            setArgs(args.map((a) => (a.id === arg.id ? { ...a, value: e.target.value } : a)))
                          }
                        />
                      )}
                      <button
                        type="button"
                        onClick={() => setArgs(args.filter((a) => a.id !== arg.id))}
                        className="p-2 text-gray-400 hover:text-red-600 transition"
                        aria-label="Remove argument"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

            </>
          )}
        </div>

        <div className="shrink-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
          {result ? (
            <Button type="primary" onClick={() => close()}>
              Done
            </Button>
          ) : (
            <>
              <Button type="tertiary" onClick={() => close()}>
                Cancel
              </Button>
              <Button type="primary" onClick={handleDeploy} disabled={deploying || !sourceName}>
                {deploying ? "Deploying…" : "Deploy"}
              </Button>
            </>
          )}
        </div>
      </div>
    </ModalV2>
  );
}

DeploySourceModal.propTypes = {
  onDeployed: PropTypes.func,
};

function ResultPanel({ result, copy, copied }) {
  const fields = [
    { key: "address", label: "Contract Address" },
    { key: "txHash", label: "Transaction Hash" },
    { key: "transactionHash", label: "Transaction Hash" },
    { key: "blockNumber", label: "Block Number" },
    { key: "chainId", label: "Chain ID" },
  ];
  const seen = new Set();
  const rows = fields
    .filter(({ key, label }) => {
      if (result?.[key] == null) return false;
      if (seen.has(label)) return false;
      seen.add(label);
      return true;
    });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-green-700">
        <CheckCircle2 size={20} />
        <span className="font-semibold">Deployment successful</span>
      </div>

      <div className="rounded-lg border border-gray-200 divide-y divide-gray-200 bg-gray-50">
        {rows.map(({ key, label }) => (
          <div key={key} className="px-4 py-3 flex items-center justify-between gap-3">
            <div className="flex flex-col min-w-0">
              <span className="text-xs uppercase tracking-wider text-gray-500">{label}</span>
              <span className="text-sm font-mono text-gray-900 break-all">{String(result[key])}</span>
            </div>
            <button
              onClick={() => copy(key, result[key])}
              className="shrink-0 p-2 text-gray-400 hover:text-blue-600 transition"
              aria-label="Copy"
            >
              {copied === key ? <CheckCircle2 size={16} className="text-green-600" /> : <Copy size={16} />}
            </button>
          </div>
        ))}
      </div>

      <details className="text-xs">
        <summary className="cursor-pointer text-gray-500 hover:text-gray-700">Raw response</summary>
        <pre className="mt-2 rounded-lg bg-gray-900 text-gray-100 px-3 py-3 font-mono overflow-x-auto">
          {JSON.stringify(result, null, 2)}
        </pre>
      </details>
    </div>
  );
}

ResultPanel.propTypes = {
  result: PropTypes.object.isRequired,
  copy: PropTypes.func.isRequired,
  copied: PropTypes.string,
};
